import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { errorService } from '@ascleo/client/services/error.service'
import {
  usePrescriptionFlowSend,
  usePrescriptionFlowSelector,
} from '../context'
import { prescriptionFlowSelectors } from '../selectors'
import type { PrescriptionRequestReason, PrescriptionSummary, PrescriptionUrgency } from '../machine'
import { docCirrusRpcClient } from '../../../../../lib/rpc/doc-cirrus.client'
import { KioskStep } from '../../../../../components/kiosk-step'
import { PrescriptionReasonSection } from './prescription-reason-section'
import { PrescriptionValidationNotice, usePrescriptionRequestSubmit } from './prescription-request-submit'
import { PrescriptionRequestSection } from './prescription-request-section'
import { PrescriptionHistorySection } from './prescription-history-section'
import { PrescriptionUrgencySection } from './prescription-urgency-section'
import { useAudio } from '../../../../../lib/use-audio'

function appendRequestLine(currentText: string | undefined, requestLine: string): string {
  const trimmedLine = requestLine.trim()
  if (!trimmedLine) return currentText ?? ''

  const trimmedText = currentText?.trim()
  if (!trimmedText) return trimmedLine

  const existingLines = trimmedText.split('\n').map((line) => line.trim())
  if (existingLines.includes(trimmedLine)) return trimmedText

  return `${trimmedText}\n${trimmedLine}`
}

/**
 * Medication request step for the prescription ordering flow.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <MedicationSelectionStep />
 * ```
 */
export function MedicationSelectionStep() {
  const { t } = useTranslation()
  useAudio('prescription-medicationSelection')
  const send = usePrescriptionFlowSend()
  const patientId = usePrescriptionFlowSelector(prescriptionFlowSelectors.patientId)
  const cachedPrescriptions = usePrescriptionFlowSelector(prescriptionFlowSelectors.prescriptions)
  const requestText = usePrescriptionFlowSelector(prescriptionFlowSelectors.requestText)
  const requestReason = usePrescriptionFlowSelector(prescriptionFlowSelectors.requestReason)
  const requestUrgency = usePrescriptionFlowSelector(prescriptionFlowSelectors.requestUrgency)
  const prescriptions = cachedPrescriptions ?? []
  const historyAlreadyLoaded = cachedPrescriptions !== undefined

  const [isHistoryLoading, setIsHistoryLoading] = useState(!historyAlreadyLoaded)
  const [historyError, setHistoryError] = useState<string | undefined>(undefined)
  const [historyAttempt, setHistoryAttempt] = useState(0)
  const hasRequestText = Boolean(requestText?.trim())
  const { validationError, clearValidation } = usePrescriptionRequestSubmit({
    requestReason,
    hasRequestText,
    onSubmit: () => send({ type: 'SUBMIT_REQUEST' }),
  })

  useEffect(function fetchPrescriptionHistory() {
    if (!patientId) {
      setIsHistoryLoading(false)
      return undefined
    }

    if (historyAlreadyLoaded) {
      setIsHistoryLoading(false)
      return undefined
    }

    const historyPatientId = patientId
    let cancelled = false

    async function fetchHistory() {
      setIsHistoryLoading(true)
      setHistoryError(undefined)

      try {
        const result = await docCirrusRpcClient.frontdesk.listPrescriptions({
          patientId: historyPatientId,
        })

        if (cancelled) return

        if (result.prescriptions.length === 0) {
          send({ type: 'PRESCRIPTIONS_EMPTY' })
        } else {
          send({ type: 'PRESCRIPTIONS_LOADED', prescriptions: result.prescriptions })
        }
      } catch (err) {
        if (cancelled) return

        // Patients only ever see the localized message (KO-01); the step
        // renders it inline, so the report stays out of the toast channel.
        const message = t('self-service.prescription.medication-selection.history-error')
        errorService.report(err, {
          layer: 'rpc',
          source: 'frontdesk.prescription.medication-history.fetch',
          userMessage: message,
          suppressToast: true
        })
        setHistoryError(message)
      } finally {
        if (!cancelled) {
          setIsHistoryLoading(false)
        }
      }
    }

    void fetchHistory()

    return function cancelPrescriptionHistoryFetch() {
      cancelled = true
    }
  }, [historyAlreadyLoaded, historyAttempt, patientId, send, t])

  const handleSelectReason = (reason: PrescriptionRequestReason) => {
    send({ type: 'SET_REASON', reason })
    clearValidation()
  }

  const handleSelectUrgency = (urgency: PrescriptionUrgency) => {
    send({ type: 'SET_URGENCY', urgency })
  }

  const handleRequestTextChange = (text: string) => {
    send({ type: 'SET_REQUEST_TEXT', text })
    clearValidation()
  }

  const handleAddPrescription = (prescription: PrescriptionSummary) => {
    const requestLine = t('self-service.prescription.medication-selection.history-request-line', {
      medicationName: prescription.medicationName,
    })
    send({
      type: 'SET_REQUEST_TEXT',
      text: appendRequestLine(requestText, requestLine),
    })
    clearValidation()
  }

  const shouldShowHistory = isHistoryLoading || Boolean(historyError) || prescriptions.length > 0

  return (
    <KioskStep
      width="default"
      title={t('self-service.prescription.medication-selection.title')}
      lead={t('self-service.prescription.medication-selection.lead')}
    >
      <PrescriptionValidationNotice message={validationError} />
      <PrescriptionReasonSection
        requestReason={requestReason}
        onSelectReason={handleSelectReason}
      />
      <PrescriptionRequestSection
        requestText={requestText}
        onRequestTextChange={handleRequestTextChange}
      >
        {shouldShowHistory && (
          <PrescriptionHistorySection
            requestText={requestText}
            isHistoryLoading={isHistoryLoading}
            historyError={historyError}
            prescriptions={prescriptions}
            onAddPrescription={handleAddPrescription}
            onRetry={() => setHistoryAttempt((attempt) => attempt + 1)}
          />
        )}
      </PrescriptionRequestSection>
      <PrescriptionUrgencySection
        requestUrgency={requestUrgency}
        onSelectUrgency={handleSelectUrgency}
      />
    </KioskStep>
  )
}
